/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable quotes */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { InputTextModule } from "primeng/inputtext";
import { PasswordModule } from "primeng/password";
import { PopoverModule } from "primeng/popover";
import { SelectModule } from "primeng/select";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Our services and models
import { SiteTier } from "../../../../core/models/site-tier.interface";
import { Site } from "../../../../core/models/site.interface";
import { SubPage } from "../../../../core/models/sub-page.interface";
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { SubscriptionUpgradeDialogComponent } from "../../../../shared/dialogs/subscription-upgrade-dialog/subscription-upgrade-dialog.component";
import {
  canAuthorizeOnly,
  canEnableTestMode,
  getFieldDisplayName,
  getPaymentMethodDisplayName,
  getPaymentMethodTypes,
  PaymentIntegration,
  PaymentIntegrationCreateDto,
  PaymentIntegrationUpdateDto,
  PAYMENT_INTEGRATION_CONFIGS,
} from "../../models/payment-integration.model";
import { PaymentIntegrationsService } from "../../services/payment-integrations.service";
import { PaymentIntegrationUpsertSkeletonComponent } from "./payment-integration-upsert-skeleton.component";

/**
 * Form control interface for strongly-typed reactive form
 */
interface PaymentIntegrationFormControls {
  PaymentMethodType: FormControl<string>;
  ApiAccount: FormControl<string | null>;
  ApiKey1: FormControl<string | null>;
  ApiKey2: FormControl<string | null>;
  ApiKey3: FormControl<string | null>;
  GatewayPaymentType: FormControl<string | null>;
  TestMode: FormControl<boolean>;
  AuthorizeOnly: FormControl<boolean>;
}

type UpsertMode = "create" | "edit";

@Component({
  selector: "app-payment-integration-upsert",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    ConfirmDialogModule,
    InputTextModule,
    PasswordModule,
    PaymentIntegrationUpsertSkeletonComponent,
    PopoverModule,
    SelectModule,
    ToastModule,
    TooltipModule,
    SubscriptionUpgradeDialogComponent,
  ],
  templateUrl: "./payment-integration-upsert.component.html",
  styleUrl: "./payment-integration-upsert.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class PaymentIntegrationUpsertComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);
  private readonly sessionService = inject(SessionService);
  private readonly qpilotService = inject(QPilotService);
  private readonly paymentIntegrationsService = inject(
    PaymentIntegrationsService,
  );
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly destroy$ = new Subject<void>();

  // Web component / embedded mode inputs
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputPaymentIntegrationId?: number;
  @Input() embeddedMode: boolean = false;

  // Mode and IDs
  readonly mode = signal<UpsertMode>("create");
  readonly siteId = signal<number | null>(null);
  readonly paymentIntegrationId = signal<number | null>(null);

  // UI State
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);

  // Site data
  readonly currentSite = signal<Site | null>(null);
  readonly isProduction = signal(false);

  // Subscription tier state (matching old QMC behavior)
  // notAllowedPaymentTypes: list of payment method types NOT allowed for this subscription tier
  readonly notAllowedPaymentTypes = signal<string[]>([]);
  readonly currentTierName = signal("Current Plan");
  readonly upgradeUrl = signal("/my-account/subscription/change-plan");
  readonly showUpgradeDialog = signal(false);

  // Form
  form!: FormGroup<PaymentIntegrationFormControls>;

  // Payment method options
  readonly paymentMethodOptions = getPaymentMethodTypes().map((type) => ({
    label: getPaymentMethodDisplayName(type),
    value: type,
  }));

  // Selected payment method state
  readonly selectedPaymentMethodType = signal<string | null>(null);

  // Current entity (for edit mode)
  private currentEntity: PaymentIntegration | null = null;

  // Getters for template
  get isEditMode(): boolean {
    return this.mode() === "edit";
  }

  get pageTitle(): string {
    return this.isEditMode
      ? `Edit Payment Integration #${this.paymentIntegrationId()}`
      : "Create Payment Integration";
  }

  // Dynamic field labels
  get apiAccountLabel(): string | null {
    const type = this.selectedPaymentMethodType();
    return type ? getFieldDisplayName(type, "ApiAccount") : null;
  }

  get apiKey1Label(): string | null {
    const type = this.selectedPaymentMethodType();
    return type ? getFieldDisplayName(type, "ApiKey1") : null;
  }

  get apiKey2Label(): string | null {
    const type = this.selectedPaymentMethodType();
    return type ? getFieldDisplayName(type, "ApiKey2") : null;
  }

  get apiKey3Label(): string | null {
    const type = this.selectedPaymentMethodType();
    return type ? getFieldDisplayName(type, "ApiKey3") : null;
  }

  // Dynamic placeholders based on field labels
  get apiAccountPlaceholder(): string {
    return this.apiAccountLabel ? `Enter your ${this.apiAccountLabel}` : "";
  }

  get apiKey1Placeholder(): string {
    return this.apiKey1Label ? `Enter your ${this.apiKey1Label}` : "";
  }

  get apiKey2Placeholder(): string {
    return this.apiKey2Label ? `Enter your ${this.apiKey2Label}` : "";
  }

  get apiKey3Placeholder(): string {
    return this.apiKey3Label ? `Enter your ${this.apiKey3Label}` : "";
  }

  get showTestModeCheckbox(): boolean {
    const type = this.selectedPaymentMethodType();
    return type ? canEnableTestMode(type) : false;
  }

  get showAuthorizeOnlyCheckbox(): boolean {
    const type = this.selectedPaymentMethodType();
    return type ? canAuthorizeOnly(type) : false;
  }

  /**
   * Check if the currently selected payment type is restricted for this subscription tier
   * (matching old QMC: notAllowedValue.includes(paymentIntegration.PaymentMethodType))
   */
  get isPaymentTypeRestricted(): boolean {
    const type = this.selectedPaymentMethodType();
    if (!type) return false;
    return this.notAllowedPaymentTypes().includes(type);
  }

  get testModeDisabled(): boolean {
    // Disable test mode checkbox if:
    // 1. Site is not in production, OR
    // 2. Selected payment type is restricted for subscription tier (force test mode)
    // Matching old QMC line 80: !isProduction || (!allowedForSubscription && notAllowedValue.includes(paymentMethodType))
    return !this.isProduction() || this.isPaymentTypeRestricted;
  }

  get testModeTooltip(): string {
    if (!this.isProduction()) {
      return "Test mode is required for non-production sites";
    }
    if (this.isPaymentTypeRestricted) {
      return "Test mode is required for this payment type on your current subscription plan";
    }
    return "Enable test mode for this integration";
  }

  ngOnInit(): void {
    // Detect embedded mode from HTML attribute
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    this.initializeForm();

    // Initialize based on mode
    if (this.embeddedMode) {
      this.initializeEmbeddedMode();
    } else {
      this.setupRouteSubscriptions();
    }

    this.setupFormSubscriptions();
  }

  /**
   * Initialize for embedded/web component mode
   */
  private initializeEmbeddedMode(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.siteId.set(parseInt(siteIdString, 10));
    }

    // Get payment integration ID from input/attribute (for edit mode)
    const integrationIdString =
      this.inputPaymentIntegrationId?.toString() ??
      element.getAttribute("input-payment-integration-id") ??
      element.getAttribute("inputPaymentIntegrationId");

    if (integrationIdString) {
      this.paymentIntegrationId.set(parseInt(integrationIdString, 10));
      this.mode.set("edit");
    } else {
      this.mode.set("create");
    }

    // Get token from attribute and convert to QPilotAccessToken
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Set the token in session service so HTTP interceptor can use it
    if (this.inputToken) {
      this.sessionService.setAccessToken(this.inputToken);
    }

    // Load data
    const siteId = this.siteId();
    if (siteId) {
      this.loadSiteData(siteId);

      // Load entity in edit mode
      if (this.isEditMode && this.paymentIntegrationId()) {
        this.loadPaymentIntegration(siteId, this.paymentIntegrationId()!);
      }
    }

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.form = this.fb.group<PaymentIntegrationFormControls>({
      PaymentMethodType: this.fb.nonNullable.control("", [Validators.required]),
      ApiAccount: this.fb.control(null),
      ApiKey1: this.fb.control(null),
      ApiKey2: this.fb.control(null),
      ApiKey3: this.fb.control(null),
      GatewayPaymentType: this.fb.control(null),
      TestMode: this.fb.nonNullable.control(true),
      AuthorizeOnly: this.fb.nonNullable.control(false),
    });
  }

  private setupRouteSubscriptions(): void {
    // Get mode from route data
    const routeData = this.route.snapshot.data;
    this.mode.set((routeData["mode"] as UpsertMode) || "create");

    // Get payment integration ID from route params (edit mode)
    const idParam = this.route.snapshot.paramMap.get("id");
    if (idParam) {
      this.paymentIntegrationId.set(parseInt(idParam, 10));
    }

    // Get site ID from parent route
    this.route.parent?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const siteIdParam = params.get("siteId");
        if (siteIdParam) {
          const siteId = parseInt(siteIdParam, 10);
          this.siteId.set(siteId);
          this.loadSiteData(siteId);

          // Load entity in edit mode
          if (this.isEditMode && this.paymentIntegrationId()) {
            this.loadPaymentIntegration(siteId, this.paymentIntegrationId()!);
          }
        }
      });
  }

  private setupFormSubscriptions(): void {
    // Watch payment method type changes
    this.form.controls.PaymentMethodType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => {
        this.selectedPaymentMethodType.set(type);
        this.onPaymentMethodTypeChange(type);
      });
  }

  private loadSiteData(siteId: number): void {
    const accessToken = this.sessionService.accessToken;
    if (!accessToken) return;

    this.qpilotService
      .getSite(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site) => {
          this.currentSite.set(site as Site);
          this.isProduction.set((site as Site).IsProduction ?? false);

          // Force test mode on non-production sites
          if (!this.isProduction()) {
            this.form.controls.TestMode.setValue(true);
            this.form.controls.TestMode.disable();
          }

          // Load subscription tier permissions (non-blocking)
          this.loadSubscriptionTierPermissions(siteId);

          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error("Failed to load site:", err);
        },
      });
  }

  /**
   * Load subscription tier permissions to determine if payment integrations are allowed
   * for the current subscription tier
   */
  private loadSubscriptionTierPermissions(siteId: number): void {
    const accessToken = this.sessionService.accessToken;
    if (!accessToken) return;

    // Page URL for payment integrations
    const pageUrl = "payment-integrations";

    // Fetch both page permissions and site tier in parallel
    forkJoin({
      pagePermissions: this.qpilotService.getPageByUrl(
        pageUrl,
        siteId.toString(),
        accessToken,
      ),
      siteTier: this.qpilotService.fetchSiteSubscriptionTier(
        accessToken,
        siteId.toString(),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ pagePermissions, siteTier }) => {
          this.handleTierPermissions(pagePermissions, siteTier);
        },
        error: (err) => {
          console.error("Error loading subscription tier permissions:", err);
          // Default to all types allowed if there's an error (graceful degradation)
          this.notAllowedPaymentTypes.set([]);
        },
      });
  }

  /**
   * Handle the tier permissions response
   * Matching old QMC behavior from dashboard-payment-integrations-create.component.ts
   */
  private handleTierPermissions(
    pagePermissions: SubPage,
    siteTier: SiteTier,
  ): void {
    // Parse allowedValue as array of NOT-allowed payment types
    // Matching old QMC line 62: this.notAllowedValue = JSON.parse(page.allowedValue);
    try {
      const notAllowed = pagePermissions.allowedValue
        ? JSON.parse(pagePermissions.allowedValue)
        : [];
      this.notAllowedPaymentTypes.set(
        Array.isArray(notAllowed) ? notAllowed : [],
      );
    } catch {
      // If parsing fails, default to empty array (all types allowed)
      this.notAllowedPaymentTypes.set([]);
    }

    // Format tier name for display
    this.currentTierName.set(
      siteTier.TierName ? `${siteTier.TierName} Plan` : "Current Plan",
    );

    // Always use our local upgrade route (API may return old QMC URL)
    // Keep the default: /my-account/subscription/change-plan

    // DO NOT show dialog immediately on page load
    // Dialog is shown at SUBMIT time if payment type is restricted (matching old QMC)

    // If a payment type is already selected and it's restricted, update test mode
    if (this.selectedPaymentMethodType() && this.isPaymentTypeRestricted) {
      this.form.controls.TestMode.setValue(true);
      this.form.controls.TestMode.disable();
    }

    this.cdr.markForCheck();
  }

  /**
   * Show the subscription upgrade dialog
   */
  openUpgradeDialog(): void {
    this.showUpgradeDialog.set(true);
  }

  /**
   * Handle upgrade dialog visibility change
   */
  onUpgradeDialogVisibleChange(visible: boolean): void {
    this.showUpgradeDialog.set(visible);
  }

  private loadPaymentIntegration(siteId: number, id: number): void {
    this.isLoading.set(true);

    this.paymentIntegrationsService
      .getPaymentIntegrationById(siteId, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entity) => {
          this.currentEntity = entity;
          this.populateFormFromEntity(entity);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error("Failed to load payment integration:", err);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to load payment integration",
            life: 5000,
          });
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private populateFormFromEntity(entity: PaymentIntegration): void {
    // Set payment method type first to trigger field visibility
    this.form.controls.PaymentMethodType.setValue(entity.PaymentMethodType);
    this.selectedPaymentMethodType.set(entity.PaymentMethodType);

    // Then set the rest of the values
    this.form.patchValue({
      ApiAccount: entity.ApiAccount,
      ApiKey1: entity.ApiKey1,
      ApiKey2: entity.ApiKey2,
      ApiKey3: entity.ApiKey3,
      GatewayPaymentType: entity.GatewayPaymentType,
      TestMode: entity.TestMode,
      AuthorizeOnly: entity.AuthorizeOnly,
    });
  }

  /**
   * Handle payment method type change
   * Matching old QMC paymentIntegrationChange() line 114-116:
   * TestMode = !isProduction || (!allowedForSubscription && notAllowedValue.includes(PaymentMethodType))
   */
  private onPaymentMethodTypeChange(type: string): void {
    // Reset fields that might not be applicable
    this.form.patchValue(
      {
        ApiAccount: null,
        ApiKey1: null,
        ApiKey2: null,
        ApiKey3: null,
        GatewayPaymentType: null,
      },
      { emitEvent: false },
    );

    // Set defaults for the new type
    const config = PAYMENT_INTEGRATION_CONFIGS[type];
    if (config) {
      // Check if this payment type is restricted for the subscription tier
      const isRestricted = this.notAllowedPaymentTypes().includes(type);

      // Set test mode based on: site production status AND subscription tier restrictions
      // Matching old QMC: TestMode = !isProduction || (notAllowedValue.includes(PaymentMethodType))
      if (!this.isProduction() || isRestricted) {
        this.form.controls.TestMode.setValue(true);
        this.form.controls.TestMode.disable();
      } else {
        this.form.controls.TestMode.enable();
      }

      // Set default authorize only
      this.form.controls.AuthorizeOnly.setValue(false);
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle form submission
   * Matching old QMC submit() lines 73-91:
   * - If payment type is restricted, show upgrade dialog
   * - If test mode is not possible for that type, block submission
   * - Otherwise, allow creation in test mode
   */
  onSubmit(): void {
    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const siteId = this.siteId();
    if (!siteId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Site ID is required",
        life: 5000,
      });
      return;
    }

    // Check if payment type is restricted for subscription tier (only in create mode)
    // Matching old QMC lines 73-79
    if (!this.isEditMode && this.isPaymentTypeRestricted) {
      // Show upgrade dialog
      this.showUpgradeDialog.set(true);

      // If test mode is not possible for this payment type, block submission
      // Matching old QMC: if(!helper.canEnableTestMode(PaymentMethodType)) { return; }
      const paymentType = this.form.controls.PaymentMethodType.value;
      if (!canEnableTestMode(paymentType)) {
        return;
      }
      // Otherwise, continue with creation in test mode (dialog is shown but doesn't block)
    }

    this.isSaving.set(true);

    if (this.isEditMode) {
      this.updatePaymentIntegration(siteId);
    } else {
      this.createPaymentIntegration(siteId);
    }
  }

  private createPaymentIntegration(siteId: number): void {
    const formValue = this.form.getRawValue();

    // Build DTO with only non-null values (matching old QMC behavior)
    const dto: PaymentIntegrationCreateDto = {
      SiteId: siteId,
      PaymentMethodType: formValue.PaymentMethodType,
      TestMode: formValue.TestMode,
    };

    // Only include optional fields if they have values
    if (formValue.AuthorizeOnly) {
      dto.AuthorizeOnly = formValue.AuthorizeOnly;
    }
    if (formValue.ApiAccount) {
      dto.ApiAccount = formValue.ApiAccount;
    }
    if (formValue.ApiKey1) {
      dto.ApiKey1 = formValue.ApiKey1;
    }
    if (formValue.ApiKey2) {
      dto.ApiKey2 = formValue.ApiKey2;
    }
    if (formValue.ApiKey3) {
      dto.ApiKey3 = formValue.ApiKey3;
    }
    if (formValue.GatewayPaymentType) {
      dto.GatewayPaymentType = formValue.GatewayPaymentType;
    }

    this.paymentIntegrationsService
      .createPaymentIntegration(siteId, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Payment integration created successfully",
          });
          this.isSaving.set(false);

          // Emit saved event in embedded mode
          if (this.embeddedMode) {
            const event = new CustomEvent("qmc-item-saved", {
              detail: {
                component: "payment-integration-upsert",
                action: "create",
                siteId: siteId,
                id: result?.Id,
              },
              bubbles: true,
              composed: true,
            });
            this.elementRef.nativeElement.dispatchEvent(event);
          }

          this.navigateToList();
        },
        error: (err) => {
          console.error("Failed to create payment integration:", err);
          // Error toast is shown globally by the API interceptor
          this.isSaving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private updatePaymentIntegration(siteId: number): void {
    const id = this.paymentIntegrationId();
    if (!id) return;

    const formValue = this.form.getRawValue();

    // Build DTO with only non-null values (matching old QMC behavior)
    const dto: PaymentIntegrationUpdateDto = {
      PaymentMethodType: formValue.PaymentMethodType,
      TestMode: formValue.TestMode,
    };

    // Only include optional fields if they have values
    if (formValue.AuthorizeOnly) {
      dto.AuthorizeOnly = formValue.AuthorizeOnly;
    }
    if (formValue.ApiAccount) {
      dto.ApiAccount = formValue.ApiAccount;
    }
    if (formValue.ApiKey1) {
      dto.ApiKey1 = formValue.ApiKey1;
    }
    if (formValue.ApiKey2) {
      dto.ApiKey2 = formValue.ApiKey2;
    }
    if (formValue.ApiKey3) {
      dto.ApiKey3 = formValue.ApiKey3;
    }
    if (formValue.GatewayPaymentType) {
      dto.GatewayPaymentType = formValue.GatewayPaymentType;
    }

    this.paymentIntegrationsService
      .updatePaymentIntegration(siteId, id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Payment integration updated successfully",
          });
          this.isSaving.set(false);

          // Emit saved event in embedded mode
          if (this.embeddedMode) {
            const event = new CustomEvent("qmc-item-saved", {
              detail: {
                component: "payment-integration-upsert",
                action: "update",
                siteId: siteId,
                id: id,
              },
              bubbles: true,
              composed: true,
            });
            this.elementRef.nativeElement.dispatchEvent(event);
          }

          this.navigateToList();
        },
        error: (err) => {
          console.error("Failed to update payment integration:", err);
          // Error toast is shown globally by the API interceptor
          this.isSaving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  onDelete(): void {
    const id = this.paymentIntegrationId();
    if (!id) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete this payment integration?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptIcon: "pi pi-trash",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Yes, delete",
      rejectLabel: "Cancel",
      accept: () => {
        this.deletePaymentIntegration();
      },
    });
  }

  private deletePaymentIntegration(): void {
    const siteId = this.siteId();
    const id = this.paymentIntegrationId();
    if (!siteId || !id) return;

    this.isDeleting.set(true);

    this.paymentIntegrationsService
      .deletePaymentIntegration(siteId, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Payment integration deleted successfully",
          });
          this.isDeleting.set(false);
          this.navigateToList();
        },
        error: (err) => {
          console.error("Failed to delete payment integration:", err);
          // Error toast is shown globally by the API interceptor
          this.isDeleting.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  onCancel(): void {
    this.navigateToList();
  }

  private navigateToList(): void {
    const siteId = this.siteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit navigation event for web component/iframe mode
      const event = new CustomEvent("qmc-navigate-back", {
        detail: {
          component: "payment-integration-upsert",
          targetComponent: "payment-integrations-list",
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Standard app mode - use Angular Router
      void this.router.navigate([
        "/widgets",
        "dashboard",
        siteId,
        "payment-integrations",
      ]);
    }
  }
}
